import React, { useCallback, useEffect, useState } from 'react';
import platformApi, { fetchPdfObjectUrl, platformErrorMessage } from '../services/platformApi';
import PageHeader from '../components/ui/PageHeader';
import LoadingScreen from '../components/ui/LoadingScreen';
import EmptyState from '../components/ui/EmptyState';
import { useToast } from '../contexts/ToastContext';
import { endOfIstDay, formatDate, formatInr } from '../utils/billingAdmin';

/** Open an invoice/credit-note PDF in a new tab. */
export async function openInvoicePdf(invoiceId) {
  const tab = window.open('', '_blank');
  try {
    const url = await fetchPdfObjectUrl(`/platform/invoices/${invoiceId}/pdf`);
    if (tab) {
      tab.location.href = url;
    } else {
      window.location.assign(url);
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (error) {
    if (tab) tab.close();
    throw error;
  }
}

function startOfIstDay(dateInput) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateInput || '')) return null;
  return new Date(`${dateInput}T00:00:00.000+05:30`).toISOString();
}

function Invoices() {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ kind: '', test: 'false', from: '', to: '', organizationId: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [opening, setOpening] = useState(null);

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: 20 };
      if (filters.kind) params.kind = filters.kind;
      if (filters.test) params.test = filters.test;
      if (filters.organizationId.trim()) params.organizationId = filters.organizationId.trim();
      const from = startOfIstDay(filters.from);
      const to = endOfIstDay(filters.to);
      if (from) params.from = from;
      if (to) params.to = to;
      const response = await platformApi.get('/platform/invoices', { params });
      setItems(response.data?.data || []);
      setMeta(response.data?.meta || { page, limit: 20, total: 0, totalPages: 0 });
    } catch (err) {
      setError(platformErrorMessage(err, 'Failed to load invoices'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const open = async (invoice) => {
    setOpening(invoice.id);
    try {
      await openInvoicePdf(invoice.id);
    } catch (err) {
      toast.error(platformErrorMessage(err, 'Could not open the PDF'));
    } finally {
      setOpening(null);
    }
  };

  if (loading && items.length === 0 && !error) return <LoadingScreen message="Loading invoices..." />;

  const set = (key) => (e) => setFilters((prev) => ({ ...prev, [key]: e.target.value }));

  return (
    <div className="page">
      <PageHeader
        title="Invoices"
        description="GST tax invoices and credit notes issued for checkout payments and refunds."
      />

      <form className="card card__body" style={{ marginBottom: '1rem' }} onSubmit={(e) => { e.preventDefault(); load(1); }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" htmlFor="inv-kind">Type</label>
            <select id="inv-kind" className="form-select" value={filters.kind} onChange={set('kind')}>
              <option value="">All</option>
              <option value="invoice">Tax invoices</option>
              <option value="credit_note">Credit notes</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" htmlFor="inv-test">Mode</label>
            <select id="inv-test" className="form-select" value={filters.test} onChange={set('test')}>
              <option value="false">Live</option>
              <option value="true">Test</option>
              <option value="">Both</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" htmlFor="inv-from">From</label>
            <input id="inv-from" type="date" className="form-input" value={filters.from} onChange={set('from')} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" htmlFor="inv-to">To</label>
            <input id="inv-to" type="date" className="form-input" value={filters.to} onChange={set('to')} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" htmlFor="inv-org">Organization ID</label>
            <input id="inv-org" className="form-input" value={filters.organizationId} onChange={set('organizationId')} placeholder="Optional" />
          </div>
          <div className="form-group" style={{ margin: 0, display: 'flex', alignItems: 'flex-end' }}>
            <button type="submit" className="btn btn--primary" disabled={loading}>{loading ? 'Loading…' : 'Apply'}</button>
          </div>
        </div>
      </form>

      {error && <div className="alert alert--error">{error}</div>}

      {items.length === 0 ? (
        <div className="card">
          <EmptyState title="No invoices" description="Invoices appear after a payment is captured and seller GST details are saved." />
        </div>
      ) : (
        <div className="card">
          <div className="table-wrapper">
            <table className="data-table data-table--responsive">
              <thead>
                <tr><th>Number</th><th>Issued</th><th>Customer</th><th>Plan</th><th>Taxable</th><th>GST</th><th>Total</th><th>PDF</th></tr>
              </thead>
              <tbody>
                {items.map((inv) => (
                  <tr key={inv.id}>
                    <td data-label="Number" className="cell-mono">
                      {inv.number}
                      {inv.kind === 'credit_note' && <div className="form-hint">Credit note for {inv.invoiceNumber || '—'}</div>}
                      {inv.test && <span className="badge badge--info" style={{ marginLeft: 6 }}>Test</span>}
                    </td>
                    <td data-label="Issued">{formatDate(inv.issuedAt)}</td>
                    <td data-label="Customer">
                      {inv.buyerName || '—'}
                      {inv.buyerGstin && <div className="form-hint cell-mono">{inv.buyerGstin}</div>}
                    </td>
                    <td data-label="Plan">{inv.planName || '—'}</td>
                    <td data-label="Taxable">{formatInr(inv.taxable)}</td>
                    <td data-label="GST">{formatInr((inv.cgst || 0) + (inv.sgst || 0) + (inv.igst || 0))}</td>
                    <td data-label="Total"><strong>{inv.kind === 'credit_note' ? '−' : ''}{formatInr(inv.total)}</strong></td>
                    <td data-label="PDF" className="cell-actions">
                      <button type="button" className="btn btn--secondary btn--sm" onClick={() => open(inv)} disabled={opening === inv.id}>
                        {opening === inv.id ? 'Opening…' : 'View'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {meta.totalPages > 1 && (
            <div className="btn-group" style={{ padding: '1rem', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn--secondary btn--sm" disabled={meta.page <= 1 || loading} onClick={() => load(meta.page - 1)}>Previous</button>
              <span className="form-hint">Page {meta.page} of {meta.totalPages} ({meta.total} total)</span>
              <button type="button" className="btn btn--secondary btn--sm" disabled={meta.page >= meta.totalPages || loading} onClick={() => load(meta.page + 1)}>Next</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Invoices;
